import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { InKindService } from '@features/in-kind/in-kind.service';
import { InKindCategoryItemStat } from '@features/in-kind/in-kind.typing';
import { AutoTableRepository, AutoTableRepositoryFactory, SimpleStringMap, TopLevelFilter, TypeSafeFormBuilder, TypeSafeFormGroup, ValueComparisonService } from '@yourcause/common';
import { I18nService } from '@yourcause/common/i18n';
import { NotifierService } from '@yourcause/common/notifier';
interface FsCalcGroup {
  [x: string]: any;
}
interface HeaderGroup {
  selectAll: boolean;
}
export interface NewInKindItemAllocation {
  id: number;
  categoryId: number;
  identification: string;
  allocatedUnits: number;
}

@Component({
  selector: 'gc-funding-source-calculator',
  templateUrl: 'funding-source-calculator.component.html',
  styleUrls: ['./funding-source-calculator.component.scss']
})
export class FundingSourceCalculatorComponent implements OnInit, OnChanges {
  @Input() description = this.i18n.translate(
    'BUDGET:textFundingSourceCalculatorDesc',
    {},
    'Calculate the funds you want to set aside for this funding source. First, filter items by your pre-defined categories and search to narrow down to the specific items you want to fund. Next, select any item(s) and enter the number of units that are represented. Please note the selected items and units are not attached to the funding source and are only being selected to help in determining an appropriate total amount for the funding source.'
  );
  @Input() newCategoryId: number;
  @Input() newInKindItemAllocation: NewInKindItemAllocation;
  @Output() onTotalChange = new EventEmitter<number>();

  formGroup: TypeSafeFormGroup<FsCalcGroup>;
  headerFormGroup: TypeSafeFormGroup<HeaderGroup>;
  repository: AutoTableRepository<InKindCategoryItemStat>;
  allItems: InKindCategoryItemStat[] = [];
  uiItems: InKindCategoryItemStat[] = [];
  topLevelFilters: TopLevelFilter[] = [];
  totalMap: SimpleStringMap<number> = {};
  itemsRemainingMap: SimpleStringMap<number> = {};
  totalSelected = 0;
  totalUnits = 0;
  totalValue = 0;
  isAfterInit = false;
  showCalculator = true;
  categoryIdsFilterValue: number[] = [];
  identificationFilterValue = '';

  constructor (
    private formBuilder: TypeSafeFormBuilder,
    private inKindService: InKindService,
    private autoTableFactory: AutoTableRepositoryFactory,
    private valueComparisonService: ValueComparisonService,
    private notifierService: NotifierService,
    private i18n: I18nService
  ) { }

  get allSelected () {
    return this.headerFormGroup.value.selectAll;
  }

  get categoryOptions () {
    return this.inKindService.categoryOptions || [];
  }

  ngOnInit () {
    this.prepCalculator();
    this.isAfterInit = true;
  }

  ngOnChanges (changes: SimpleChanges) {
    if (this.isAfterInit) {
      if (changes.newInKindItemAllocation && !!this.newInKindItemAllocation) {
        if (
          this.categoryIdsFilterValue.length > 0 &&
          this.categoryIdsFilterValue.includes(this.newInKindItemAllocation.categoryId)
        ) {
          this.categoryIdsFilterValue = [
            ...this.categoryIdsFilterValue,
            this.newInKindItemAllocation.categoryId
          ];
        }
        this.resetView();
      } else if (changes.newCategoryId && !!this.newCategoryId) {
        if (this.categoryIdsFilterValue.length > 0) {
          // if we are filtering by category, make sure new category gets selected
          this.categoryIdsFilterValue = [
            ...this.categoryIdsFilterValue,
            this.newCategoryId
          ];
        }
        this.resetView();
      }
    }
  }

  prepCalculator (isReset = false) {
    this.setHeaderFormGroup(isReset);
    this.setTopLevelFilters();
    this.setAllItems();
    this.setItemsRemainingMap();
    this.setUiItems(isReset);
    this.repository = this.autoTableFactory.create({
      key: 'ITEM_CALCULATOR',
      columns: [],
      notifier: this.notifierService,
      valueComparisonService: this.valueComparisonService,
      rows: this.uiItems,
      rowsPerPage: 1000000,
      formGroup: this.formGroup,
      topLevelFilters: this.topLevelFilters,
      skipAddToState: true
    });
    this.setTotalsLine();
  }

  trackBy (_index: number, row: InKindCategoryItemStat) {
    return row.identification;
  }

  resetView () {
    this.showCalculator = false;
    this.prepCalculator(true);
    setTimeout(() => {
      this.showCalculator = true;
    });
  }

  setAllItems () {
    this.allItems = this.inKindService.allItems?.filter((item) => item.inUse) ?? [];
  }

  setUiItems (isReset = false) {
    if (isReset) {
      const ids = this.uiItems.map((item) => item.identification);
      const shouldAddNewItem = !!this.newInKindItemAllocation &&
        !ids.includes(this.newInKindItemAllocation.identification);
      if (shouldAddNewItem) {
        const foundNewItem = this.allItems.find((item) => {
          return item.identification === this.newInKindItemAllocation.identification;
        });
        if (foundNewItem) {
          const newUiItem = {
            ...foundNewItem,
            selected: this.newInKindItemAllocation.allocatedUnits > 0,
            unitsEntered: this.newInKindItemAllocation.allocatedUnits || 0
          };
          this.uiItems = [
            ...this.uiItems,
            newUiItem
          ];
          this.setTotalMapPerRow(newUiItem, newUiItem.unitsEntered);
        }
      }
    } else {
      this.uiItems = this.allItems.map((item) => {
        return {
          ...item,
          unitsEntered: 0,
          selected: false
        };
      }) ?? [];
    }
  }

  setTopLevelFilters () {
    const categoryFilter = new TopLevelFilter(
      'checkboxDropdown',
      'categoryId',
      this.categoryIdsFilterValue,
      this.i18n.translate(
        'GLOBAL:textSearchForACategory',
        {},
        'Search for a category'
      ),
      {
        selectOptions: this.categoryOptions,
        filterObjectName: this.i18n.translate(
          'GLOBAL:textCategory', {}
        ).toLowerCase(),
        filterObjectNamePlural: this.i18n.translate(
          'GLOBAL:textCategories', {}
        ).toLowerCase()
      },
      undefined,
      undefined,
      true
    );
    this.topLevelFilters = [
      new TopLevelFilter(
        'text',
        'identification',
        this.identificationFilterValue,
        this.i18n.translate(
          'GLOBAL:textSearchForAnItem',
          {},
          'Search for an item'
        ),
        undefined,
        undefined,
        [{
          column: 'identification',
          filterType: 'cn'
        }, {
          column: 'name',
          filterType: 'cn'
        }]
      )
    ];
    if (this.categoryOptions.length > 1) {
      this.topLevelFilters.unshift(categoryFilter);
    }
    const repo = this.autoTableFactory.getRepository('ITEM_CALCULATOR');
    this.formGroup = this.formBuilder.group(
      this.topLevelFilters.reduce((formGroup, filter) => ({
        ...formGroup,
        [filter.column]: [filter.value]
      }), {})
    );
    if (
      repo &&
      repo.topLevelFormGroup &&
      (this.formGroup !== repo.topLevelFormGroup)
    ) {
      this.formGroup = repo.topLevelFormGroup;
    }
  }

  onTopLevelFilterChange (filter: TopLevelFilter) {
    if (filter.column === 'categoryId') {
      this.categoryIdsFilterValue = filter.value;
    } else if (filter.column === 'identification') {
      this.identificationFilterValue = filter.value;
    }
  }

  setHeaderFormGroup (isReset = false) {
    this.headerFormGroup = this.formBuilder.group<HeaderGroup>({
      selectAll: [isReset ? this.headerFormGroup.value.selectAll : false]
    });
  }

  setItemsRemainingMap () {
    this.allItems.forEach((item) => {
      this.itemsRemainingMap[item.identification] = item.unitsRemaining || 0;
    });
  }

  setTotalMapPerRow (
    item: InKindCategoryItemStat,
    unitsEntered = item.unitsEntered
  ) {
    let changedToSelected = false; // If true, no diff necessary because it was not already included in total
    if (+unitsEntered > 0) {
      if (!item.selected) {
        item.selected = true;
        changedToSelected = true;
        this.totalSelected = this.totalSelected + 1;
      }
    } else {
      if (item.selected) {
        item.selected = false;
        this.totalSelected = this.totalSelected - 1;
      }
    }
    if (item.selected && (+item.unitsEntered !== +unitsEntered)) {
      const valueDiff = changedToSelected ?
        (+unitsEntered * item.value) :
        (+unitsEntered * item.value) - (+item.unitsEntered * item.value);
      const unitDiff = changedToSelected ?
        +unitsEntered :
        +unitsEntered - +item.unitsEntered;
      this.totalUnits = this.totalUnits + unitDiff;
      this.totalValue = this.totalValue + valueDiff;
      this.onTotalChange.emit(this.totalValue);
    }
    this.totalMap[item.identification] = +unitsEntered * +item.value;
  }

  setTotalsLine () {
    let totalUnits = 0;
    let totalValue = 0;
    let allItemsSelected = true;
    this.totalSelected = this.uiItems.reduce((acc, item) => {
      if (!item.selected) {
        allItemsSelected = false;
      }
      const selected = item.selected;
      if (selected) {
        const units = item.unitsEntered;
        totalUnits = totalUnits + +units;
        const totalRow = +units * item.value;
        totalValue = totalValue + totalRow;

        return acc + 1;
      }

      return acc;
    }, 0);
    this.totalUnits = totalUnits;
    this.totalValue = totalValue;
    this.headerFormGroup.get('selectAll').setValue(
      allItemsSelected
    );
    this.onTotalChange.emit(this.totalValue);
  }

  toggleSelectAll () {
    this.repository.currentSet.forEach((item) => {
      item.selected = this.allSelected;
    });
    this.setTotalsLine();
  }
}
